import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { waLink, waMessages } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";
import { Link2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const GRADES = ["Initial I", "Initial II", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8", "Diploma"];

const EMPTY = { name: "", gender: "L", birth_date: "", parent_name: "", phone: "", address: "", class_id: "none", grade: "Initial I", status: "Aktif", notes: "" };

export default function Students() {
  const { user } = useAuth();
  const canManage = ["administrator", "customer_service"].includes(user?.role);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const params = {};
    if (search) params.search = search;
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/students", { params }).then((res) => setStudents(res.data)).catch(() => {});
  };

  useEffect(() => { load(); }, [search, statusFilter]);
  useEffect(() => { api.get("/classes").then((res) => setClasses(res.data)).catch(() => {}); }, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, gender: s.gender || "L", birth_date: s.birth_date || "", parent_name: s.parent_name || "",
      phone: s.phone || "", address: s.address || "", class_id: s.class_id || "none", grade: s.grade || "Initial I", status: s.status, notes: s.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = { ...form, class_id: form.class_id === "none" ? null : form.class_id };
    try {
      if (editing) {
        await api.put(`/students/${editing.id}`, payload);
        toast.success("Data siswa diperbarui");
      } else {
        await api.post("/students", payload);
        toast.success("Siswa baru ditambahkan");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menyimpan data siswa"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Hapus siswa "${s.name}" beserta riwayat pembayarannya?`)) return;
    try {
      await api.delete(`/students/${s.id}`);
      toast.success("Siswa dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err, "Gagal menghapus siswa"));
    }
  };

  return (
    <div className="space-y-6" data-testid="students-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Data Siswa</h1>
          <p className="mt-1 text-sm text-muted-foreground">{students.length} siswa terdaftar.</p>
        </div>
        {canManage && (
          <Button onClick={openAdd} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-student-button">
            <Plus className="h-4 w-4" /> Tambah Siswa
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari nama siswa..." className="pl-9" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="student-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="student-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="Aktif">Aktif</SelectItem>
            <SelectItem value="Nonaktif">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead>
                <TableHead>Kelas</TableHead>
                <TableHead>Grade</TableHead>
                <TableHead>Orang Tua / Wali</TableHead>
                <TableHead>Telepon</TableHead>
                <TableHead className="text-center">Pertemuan/Bulan</TableHead>
                <TableHead className="text-right">SPP / Bulan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-muted-foreground" data-testid="students-empty">
                    Belum ada data siswa.
                  </TableCell>
                </TableRow>
              ) : students.map((s) => (
                <TableRow key={s.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`student-row-${s.id}`}>
                  <TableCell className="font-semibold">{s.name}</TableCell>
                  <TableCell>{s.class_name}</TableCell>
                  <TableCell>
                    <Badge className="bg-primary/10 text-primary hover:bg-primary/10" data-testid={`student-grade-${s.id}`}>{s.grade}</Badge>
                  </TableCell>
                  <TableCell>{s.parent_name || "-"}</TableCell>
                  <TableCell>{s.phone || "-"}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary" data-testid={`student-meetings-${s.id}`}>{s.meetings_per_month || 0}x</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">{rupiah(s.monthly_fee)}</TableCell>
                  <TableCell>
                    <Badge className={s.status === "Aktif" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-slate-200 text-slate-600 hover:bg-slate-200"}>
                      {s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Salin tautan portal orang tua"
                      data-testid={`portal-student-${s.id}`}
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/portal/${s.id}`);
                        toast.success("Tautan portal disalin — kirim ke orang tua via WhatsApp");
                      }}
                    >
                      <Link2 className="h-4 w-4 text-primary" />
                    </Button>
                    {s.phone && (
                      <a
                        href={waLink(s.phone, waMessages.general({ parentName: s.parent_name, studentName: s.name }))}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`wa-student-${s.id}`}
                      >
                        <Button variant="ghost" size="icon" title="Chat WhatsApp orang tua">
                          <MessageCircle className="h-4 w-4 text-emerald-600" />
                        </Button>
                      </a>
                    )}
                    {canManage && (
                      <>
                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-student-${s.id}`}>
                          <Pencil className="h-4 w-4 text-primary" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(s)} data-testid={`delete-student-${s.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="student-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Siswa" : "Tambah Siswa Baru"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Nama Lengkap *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Jenis Kelamin</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="student-gender-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="L">Laki-laki</SelectItem>
                  <SelectItem value="P">Perempuan</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tanggal Lahir</Label>
              <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="student-birthdate-input" />
            </div>
            <div className="space-y-2">
              <Label>Orang Tua / Wali</Label>
              <Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} data-testid="student-parent-input" />
            </div>
            <div className="space-y-2">
              <Label>Telepon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="student-phone-input" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Alamat</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="student-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Kelas</Label>
              <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                <SelectTrigger data-testid="student-class-select"><SelectValue placeholder="Pilih kelas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Belum ada kelas</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.instrument})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Grade / Tingkat</Label>
              <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                <SelectTrigger data-testid="student-grade-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="student-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aktif">Aktif</SelectItem>
                  <SelectItem value="Nonaktif">Nonaktif</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="student-cancel-button">Batal</Button>
            <Button onClick={save} disabled={saving || !form.name} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="student-save-button">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
